import { useState } from 'react';
import { Card, Button } from '@opendoor/bricks/core';
import { checkTelValidity } from '@opendoor/bricks/core/Input/helpers';
import { PrivacyPolicy } from '@opendoor/cinderblocks/platform/PrivacyPolicy';
import { showToast } from '@opendoor/bricks/complex/Toast';

import { useQualificationContext } from 'shared/helpers/QualificationContext';
import { getLeadInfo } from 'sellDirect/helpers/useCalendlyParams';
import { useScheduleOfferReview } from 'sellDirect/graphql/useScheduleOfferReview';
import { PrivacyPolicyDescription } from 'shared/components/PrivacyPolicyDescription';
import { ORVAInfoCard } from 'sellDirect/components/homeWalkthrough/ORVAInfoCard';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import { ORVAData } from 'sellDirect/OfferContext';
import { useVirtualAssessmentData } from 'sellDirect/graphql/useVirtualAssessmentData';

/** Renders the ORVA schedule UI. */
export const ORVASchedule: React.FC<{
  orvaData: ORVAData;
}> = ({ orvaData }) => {
  const { lead } = useQualificationContext();
  const { phone } = getLeadInfo(lead);
  const { earliestCalendly, refetchSucceeded, refetch, refetching } = orvaData;
  const { refetch: refetchORVAData } = useVirtualAssessmentData();
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>(phone);
  const { isMobile } = useDevice();

  const { scheduleORVA, loading: scheduleOfferReviewLoading } = useScheduleOfferReview(
    orvaData.calendar,
  );

  const confirmDisabled = !phoneNumber || !checkTelValidity(phoneNumber) || !earliestCalendly;

  /** Tries to schedule an ORVA. If it fails it shows a toast error. */
  const onScheduleClick = async () => {
    if (phoneNumber && earliestCalendly) {
      const result = await scheduleORVA(phoneNumber, earliestCalendly);

      // If there's no errors, refetch ORVA data
      if (!result?.errors?.length) {
        refetch();
        refetchORVAData();
      } else {
        // If there's errors, show toast and ask customer to try again
        showToast(
          'error',
          'Oops, something went wrong, please try a different time. If the error persists, please contact your experience partner.',
          'cake-schedule-orva-error-toast',
          { toastOptions: { position: 'bottom-left' } },
        );
      }
    }
  };

  return (
    <Card
      variant="tertiary"
      flexDir="column"
      alignItems="flex-start"
      justifyContent="flex-start"
      py={8}
      px={[7, 7, 8]}
      borderRadius={['none', 'none', 'semiRounded']}
      borderColor={['transparent', 'transparent', 'neutrals20']}
    >
      <ORVAInfoCard
        phoneNumber={phoneNumber}
        onPhoneNumberChange={(_event, phoneNumber) => {
          setPhoneNumber(phoneNumber);
        }}
        hasError={!refetchSucceeded}
        disable={refetching}
        orvaData={orvaData}
      />

      {!orvaData.earliestAvailabilityError && (
        <>
          {!isMobile && <PrivacyPolicyDescription mt={6} ctaText="Next" />}

          <Button
            mt={[5, null, '26px']}
            mb="5"
            width={['100%', null, '310px']}
            aria-label={`Go to Next step and Schedule a video walkthrough for ${phoneNumber}`}
            analyticsName="cake-orva-scheduled"
            onClick={onScheduleClick}
            disabled={confirmDisabled || scheduleOfferReviewLoading || refetching}
            loading={scheduleOfferReviewLoading || refetching}
          >
            Next
          </Button>
          <PrivacyPolicy />
          {isMobile && <PrivacyPolicyDescription mt={5} ctaText="Next" />}
        </>
      )}
    </Card>
  );
};
